import {
  BridgeTxTableRow,
  BridgeTxApiSingleResult,
  SourceChain,
  Network,
  CroChain,
  CronosChain,
  EthChainId,
  TokenAsset,
  BridgeTxApiSingleResultStatus,
  CosmosHubChain,
  TerraChain,
  JunoChain,
  AkashChain,
  CantoChain,
  IrisChain,
  CelestiaChain,
} from '@types';
import { shortenAddress } from '@lib/utils';
import staticConfig from '@config/static-config';
import { bech32ToHex } from '@lib/utils';
import { ethers } from 'ethers';

const { explorers } = staticConfig;

// For formatting bridge txn API response to readable format

const convertAddress = (isCronos: boolean, address: string) => {
  if (isCronos) {
    const checksumHexAddress = ethers.utils.getAddress(bech32ToHex(address));
    return shortenAddress(checksumHexAddress);
  }
  return shortenAddress(address);
};

// TODO: remove below cronos-specific handling when explorer can support cosmos tx https://mcoproduct.atlassian.net/browse/CDP-272
const handleDestinationHash = (
  isCronos: boolean,
  txnHash: string | undefined
) => {
  if (isCronos) return '';
  return txnHash || '';
};

export const convertApiDataToReadableFormat = (
  rawValues: BridgeTxApiSingleResult,
  isTestnet: boolean
): BridgeTxTableRow => {
  const sourceChain = rawValues.sourceChain;
  const destinationChain = rawValues.destinationChain;

  return {
    status: formatBridgeTxApiStatus(rawValues.status),
    from: {
      address: convertAddress(
        sourceChain === SourceChain.cronos &&
          destinationChain !== SourceChain.ethereum,
        rawValues.sourceAddress
      ),
      network: sourceChain.replace('-', ' '),
    },
    to: {
      address: convertAddress(
        destinationChain === SourceChain.cronos &&
          sourceChain !== SourceChain.ethereum,
        rawValues.destinationAddress
      ),
      network: destinationChain.replace('-', ' '),
    },
    amountAndDateTime: {
      amount: `${parseFloat(rawValues.displayAmount)} ${
        rawValues.displayDenom
      }`,
      dateTime: formatBridgeTxApiDate(rawValues.sourceBlockTime),
    },
    transactionHash: {
      source: rawValues.sourceTransactionId,
      sourceExplorer: getExplorerFromChain(
        sourceChain as SourceChain,
        isTestnet
      ),
      destination: handleDestinationHash(
        destinationChain === 'Cronos',
        rawValues.destinationTransactionId
      ),
      destinationExplorer: getExplorerFromChain(
        destinationChain as SourceChain,
        isTestnet
      ),
    },
  };
};

// Temporary measure for CosmosHub txns
export const createCosmosTxReceiptWorkaround = (
  sourceChain: SourceChain,
  sourceAddress: string,
  destinationChain: SourceChain,
  destinationAddress: string,
  displayAmount: string,
  displayDenom: string,
  sourceTransactionId: string,
  isTestnet: boolean,
  destinationTransactionId?: string
): BridgeTxTableRow => {
  return {
    isTestnet,
    status: formatBridgeTxApiStatus(BridgeTxApiSingleResultStatus.beta),
    from: {
      address: shortenAddress(sourceAddress), // No need to convert for metamask address as they are already converted
      network: sourceChain.replace('-', ' '),
    },
    to: {
      address: convertAddress(
        destinationChain === SourceChain.cronos &&
          sourceChain !== SourceChain.ethereum,
        destinationAddress
      ),
      network: destinationChain.replace('-', ' '),
    },
    amountAndDateTime: {
      amount: `${parseFloat(displayAmount)} ${displayDenom}`,
      dateTime: formatBridgeTxApiDate(Date.now()),
    },
    transactionHash: {
      source: sourceTransactionId,
      sourceExplorer: getExplorerFromChain(
        sourceChain as SourceChain,
        isTestnet
      ),
      destination: handleDestinationHash(
        destinationChain === 'Cronos',
        destinationTransactionId
      ),
      destinationExplorer: getExplorerFromChain(
        destinationChain as SourceChain,
        isTestnet
      ),
    },
  };
};

export const findDecimalForCurrency = (
  sourceChain: string,
  currencySymbol: string,
  tokenAssets: TokenAsset[] | null
): number => {
  if (sourceChain === SourceChain.cronos && currencySymbol === 'CRO') return 18;
  return (
    tokenAssets?.find((value) => value.symbol === currencySymbol)?.decimals ?? 0
  );
};

export const formatBridgeTxApiDate = (unixts: string | number) => {
  const date = new Date(unixts);
  return (
    date.getFullYear() +
    '/' +
    (date.getMonth() + 1) +
    '/' +
    date.getDate() +
    ' ' +
    date.getUTCHours() +
    ':' +
    date.getMinutes() +
    ':' +
    date.getSeconds() +
    ' GMT'
  );
};

export const formatBridgeTxApiStatus = (
  status: BridgeTxApiSingleResultStatus
) => {
  switch (status) {
    case BridgeTxApiSingleResultStatus.counterpartyConfirmed:
      return 'Completed';
    case BridgeTxApiSingleResultStatus.counterpartyRejected:
    case BridgeTxApiSingleResultStatus.failed:
      return 'Failed';
    case BridgeTxApiSingleResultStatus.cancelled:
      return 'Cancelled';
    case BridgeTxApiSingleResultStatus.beta:
      return 'Beta';
    case BridgeTxApiSingleResultStatus.pending:
    default:
      return 'Pending';
  }
};

export const getExplorerFromChain = (
  sourceChain: SourceChain,
  isTestnet: boolean
): string => {
  switch (sourceChain) {
    case SourceChain.cronos:
      return isTestnet
        ? explorers[Network.Cronos][CronosChain.gravityTestnet]
        : explorers[Network.Cronos][CronosChain.mainnet];
    case SourceChain.cosmos:
      return isTestnet
        ? explorers[Network.Cosmos][CosmosHubChain.testnet]
        : explorers[Network.Cosmos][CosmosHubChain.mainnet];
    case SourceChain.terra:
      return isTestnet
        ? explorers[Network.Terra][TerraChain.testnet]
        : explorers[Network.Terra][TerraChain.mainnet];
    case SourceChain.juno:
      return isTestnet
        ? explorers[Network.Juno][JunoChain.testnet]
        : explorers[Network.Juno][JunoChain.mainnet];
    case SourceChain.akash:
      return isTestnet
        ? explorers[Network.Akash][AkashChain.testnet]
        : explorers[Network.Akash][AkashChain.mainnet];
    case SourceChain.canto:
      return isTestnet
        ? explorers[Network.Canto][CantoChain.testnet]
        : explorers[Network.Canto][CantoChain.mainnet];
    case SourceChain.iris:
      return isTestnet
        ? explorers[Network.Iris][IrisChain.testnet]
        : explorers[Network.Iris][IrisChain.mainnet];
    case SourceChain.celestia:
      return isTestnet
        ? explorers[Network.Celestia][CelestiaChain.testnet]
        : explorers[Network.Celestia][CelestiaChain.mainnet];
    case SourceChain.cryptoOrg:
      return isTestnet
        ? explorers[Network.CryptoOrg][CroChain.testnet4]
        : explorers[Network.CryptoOrg][CroChain.mainnet];
    case SourceChain.ethereum:
      return isTestnet
        ? explorers[Network.Ethereum][EthChainId.Goerli]
        : explorers[Network.Ethereum][EthChainId.Mainnet];
    default:
      return '';
  }
};
